import { useState, useEffect, useCallback } from "react";
import { useSubscription } from "@/hooks/useSubscription";

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = "bn_trial_prompt_dismissed_at";
/** How long to suppress the prompt after the user dismisses it (24 hours). */
const DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function daysUntil(ms: number): number {
  return Math.max(0, Math.ceil((ms - Date.now()) / (1000 * 60 * 60 * 24)));
}

function getDismissedAt(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
}

function setDismissedAt(): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // localStorage may be unavailable in private browsing / SSR guard
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useTrialConversionPrompt
 *
 * Controls when the TrialConversionModal should proactively appear.
 *
 * Triggers when:
 *   - The user is currently on a free trial (status === "trialing")
 *   - The trial has ≤ `threshold` days remaining (default: 3)
 *   - The user has not dismissed the prompt within the last 24 hours
 *
 * The 24-hour dismissal cooldown is persisted in localStorage so users are
 * not spammed across page refreshes or route changes.
 *
 * @param threshold  Days remaining at which to start prompting (default 3)
 *
 * @example
 * ```tsx
 * function DashboardLayout({ children }) {
 *   const { isOpen, dismiss } = useTrialConversionPrompt();
 *   return (
 *     <>
 *       {children}
 *       <TrialConversionModal open={isOpen} onClose={dismiss} />
 *     </>
 *   );
 * }
 * ```
 */
export function useTrialConversionPrompt(threshold = 3) {
  const { isTrialing, trialEndsAt, isLoading, isAdmin } = useSubscription();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Wait for subscription data to load; never show for admins
    if (isLoading || isAdmin || !isTrialing || !trialEndsAt) return;

    const daysLeft = daysUntil(trialEndsAt);
    if (daysLeft > threshold) return;

    // Respect the dismissal cooldown
    const dismissedAt = getDismissedAt();
    if (dismissedAt !== null && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) {
      return;
    }

    setIsOpen(true);
  }, [isLoading, isAdmin, isTrialing, trialEndsAt, threshold]);

  /** Close the modal and start the 24-hour cooldown. */
  const dismiss = useCallback(() => {
    setIsOpen(false);
    setDismissedAt();
  }, []);

  /** Manually open the modal (e.g. from a CTA button). Does not affect the cooldown. */
  const open = useCallback(() => setIsOpen(true), []);

  return { isOpen, open, dismiss };
}
